/* eslint-disable max-lines */
import { EventType } from '@ag-ui/core';

import { CitationStreamParser } from '../utils/citation-stream.utils';

import type {
  ChatUIMessage,
  ChatUIMessageChunk,
  CitationSourceEvent,
  CitationSourcesEvent,
  CitedChatUIMessageChunk,
  StreamingCitationSources,
  ToolDisplayNames,
  ToolDisplayNamesUIMessageChunk,
} from '../types';
import type {
  Message as AGUIMessage,
  BaseEvent,
  CustomEvent,
  RunErrorEvent,
  RunStartedEvent,
  TextMessageChunkEvent,
  TextMessageContentEvent,
  TextMessageStartEvent,
  ToolCallResultEvent,
  ToolCallStartEvent,
} from '@ag-ui/core';
import type { ChatTransport } from 'ai';

export type AGUITransportOptions = {
  api: string;
  credentials?: RequestCredentials;
};

export class AGUITransport<
  TMessage extends ChatUIMessage = ChatUIMessage,
> implements ChatTransport<TMessage> {
  protected api: string;
  protected credentials?: RequestCredentials;
  /** The run ID from the current RUN_STARTED. */
  private runId: string | null = null;
  /** The real assistant message ID from TEXT_MESSAGE_START. */
  private textMessageId: string | null = null;
  private citationParser = new CitationStreamParser();
  private accumulatedCitations: StreamingCitationSources = [];
  private accumulatedToolDisplayNames: ToolDisplayNames = {};

  constructor(options: AGUITransportOptions) {
    this.api = options.api;
    this.credentials = options.credentials;
  }

  /** The current run ID. Available from RUN_STARTED until RUN_FINISHED. */
  protected get currentRunId(): string {
    if (this.runId === null) {
      throw new Error('Unexpected state - no run in progress');
    }
    return this.runId;
  }

  /** The current text message ID. Available from TEXT_MESSAGE_START until RUN_FINISHED. */
  protected get currentTextMessageId(): string | null {
    return this.textMessageId;
  }

  private resetCitationState(): void {
    this.citationParser.reset();
    this.accumulatedCitations = [];
  }

  reconnectToStream: ChatTransport<TMessage>['reconnectToStream'] = () => {
    return Promise.resolve(null);
  };

  protected buildRequestBody(
    chatId: string,
    messages: AGUIMessage[],
  ): Record<string, unknown> {
    return {
      thread_id: chatId,
      messages,
    };
  }

  sendMessages: ChatTransport<TMessage>['sendMessages'] = async ({
    chatId,
    messages,
    abortSignal,
    trigger,
    messageId,
  }) => {
    let messagesToSend: TMessage[];

    if (trigger === 'regenerate-message') {
      // For regeneration, send the last user message to regenerate the response
      const lastUserMessage = [...messages]
        .reverse()
        .find((m) => m.role === 'user');
      messagesToSend = lastUserMessage ? [lastUserMessage] : [];
    } else {
      // For submit-message, only send pending messages
      messagesToSend = messages.filter(
        (msg) =>
          msg.metadata &&
          typeof msg.metadata === 'object' &&
          'pending' in msg.metadata &&
          msg.metadata.pending,
      );
    }

    // Clear pending flag to prevent re-sending
    // Note: We are not too concerned about being able to retry multiple messages, as this
    // is experimental.
    messagesToSend.forEach((msg) => {
      if (
        msg.metadata &&
        typeof msg.metadata === 'object' &&
        'pending' in msg.metadata
      ) {
        (msg.metadata as { pending: boolean }).pending = false;
      }
    });

    const aguiMessages = this.convertToAGUIMessages(messagesToSend);

    const requestBody = this.buildRequestBody(chatId, aguiMessages);

    const finalHeaders = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };

    const response = await fetch(this.api, {
      method: 'POST',
      headers: finalHeaders,
      body: JSON.stringify(requestBody),
      credentials: this.credentials,
      signal: abortSignal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Response body is not readable');
    }

    return this.createAISDKStream(response.body);
  };

  protected convertToAGUIMessages(messages: TMessage[]): AGUIMessage[] {
    return messages.map((message): AGUIMessage => {
      const textContent =
        message.parts
          ?.filter((part) => part.type === 'text')
          .map((part) => (part as { text: string }).text)
          .join(' ') || '';

      switch (message.role) {
        case 'user':
        case 'assistant':
        case 'system':
          return {
            id: message.id,
            role: message.role,
            content: textContent,
          };
        default:
          return {
            id: message.id,
            role: 'user',
            content: textContent,
          };
      }
    });
  }

  // Batching interval for text-delta events (ms)
  // Reduces state updates from potentially 100+/sec to ~10/sec
  private static readonly DELTA_BATCH_INTERVAL_MS = 100;

  protected createAISDKStream(
    responseStream: ReadableStream<Uint8Array>,
  ): ReadableStream<ChatUIMessageChunk> {
    const decoder = new TextDecoder();
    let buffer = '';
    const self = this;

    // Batching state for text-delta events
    let deltaBuffer = '';
    /** The run ID captured when deltas were buffered, so flushes after RUN_FINISHED still work. */
    let deltaRunId: string | null = null;
    let lastDeltaFlush = Date.now();
    let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let streamClosed = false;

    const flushDeltaBuffer = (
      controller: ReadableStreamDefaultController<ChatUIMessageChunk>,
    ) => {
      // Guard against post-close flushes (e.g., from scheduled timeout)
      if (streamClosed) return;
      if (flushTimeoutId) {
        clearTimeout(flushTimeoutId);
        flushTimeoutId = null;
      }
      if (deltaBuffer && deltaRunId !== null) {
        controller.enqueue({
          type: 'text-delta',
          delta: deltaBuffer,
          id: deltaRunId,
        });
        deltaBuffer = '';
        deltaRunId = null;
        lastDeltaFlush = Date.now();
      }
    };

    let reader: ReadableStreamDefaultReader<Uint8Array>;

    return new ReadableStream({
      cancel() {
        streamClosed = true;
        if (flushTimeoutId) {
          clearTimeout(flushTimeoutId);
          flushTimeoutId = null;
        }
        reader?.cancel().catch(() => {});
      },
      async start(controller) {
        reader = responseStream.getReader();

        try {
          let done = false;
          while (!done && !streamClosed) {
            const result = await reader.read();
            done = result.done;
            if (done) break;
            const value = result.value;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              const trimmedLine = line.trim();
              if (trimmedLine === '' || !trimmedLine.startsWith('data: '))
                continue;

              try {
                const eventData = trimmedLine.slice(6);

                if (eventData === '[DONE]' || eventData === 'data: [DONE]') {
                  if (!streamClosed) {
                    // Flush any remaining buffered deltas before closing
                    flushDeltaBuffer(controller);
                    streamClosed = true;
                    controller.close();
                  }
                  return;
                }

                if (eventData.trim() === '') continue;

                let event: BaseEvent;
                try {
                  event = JSON.parse(eventData);
                } catch (jsonError) {
                  continue;
                }

                if (!event || typeof event.type !== 'string') {
                  continue;
                }

                const chunks = self.handleAGUIEvent(event);

                if (chunks) {
                  const chunksArray = Array.isArray(chunks) ? chunks : [chunks];

                  for (const chunk of chunksArray) {
                    // Batch text-delta events to reduce state update frequency
                    if (chunk.type === 'text-delta') {
                      deltaBuffer += chunk.delta;
                      deltaRunId = chunk.id;

                      const now = Date.now();
                      const timeSinceLastFlush = now - lastDeltaFlush;

                      if (
                        timeSinceLastFlush >=
                        AGUITransport.DELTA_BATCH_INTERVAL_MS
                      ) {
                        // Enough time has passed, flush immediately
                        flushDeltaBuffer(controller);
                      } else if (!flushTimeoutId) {
                        // Schedule a flush for later
                        const delay =
                          AGUITransport.DELTA_BATCH_INTERVAL_MS -
                          timeSinceLastFlush;
                        flushTimeoutId = setTimeout(() => {
                          flushDeltaBuffer(controller);
                        }, delay);
                      }
                    } else {
                      // For non-delta events, flush pending deltas first to maintain order
                      flushDeltaBuffer(controller);
                      if (!streamClosed) {
                        controller.enqueue(chunk);
                      }
                    }
                  }
                }
              } catch (parseError) {
                // Failed to process AG-UI event line
              }
            }
          }
        } catch (error) {
          streamClosed = true;
          try {
            controller.error(error);
          } catch {
            // Stream already closed, cancelled, or errored
          }
        } finally {
          if (flushTimeoutId) {
            clearTimeout(flushTimeoutId);
            flushTimeoutId = null;
          }
          // Only flush and close if not already closed (by [DONE], cancel, or error)
          if (!streamClosed) {
            try {
              flushDeltaBuffer(controller);
              streamClosed = true;
              controller.close();
            } catch {
              // Stream already closed or errored
            }
          }
          reader.releaseLock();
        }
      },
    });
  }

  protected handleAGUIEvent(
    event: BaseEvent,
  ): ChatUIMessageChunk | ChatUIMessageChunk[] | null {
    switch (event.type) {
      case EventType.RUN_STARTED: {
        const runStartedEvent = event as RunStartedEvent;
        this.runId = runStartedEvent.runId;
        this.textMessageId = null;
        this.accumulatedToolDisplayNames = {};
        return [{ type: 'start', messageId: this.runId }];
      }

      case EventType.TEXT_MESSAGE_START: {
        const startEvent = event as TextMessageStartEvent;
        if (startEvent.messageId) {
          this.textMessageId = startEvent.messageId;
        }
        this.resetCitationState();
        // Store the real backend messageId as metadata on the runId message.
        // The message keeps runId as its ID throughout streaming.
        const chunks: ChatUIMessageChunk[] = [];
        chunks.push({ type: 'text-start', id: this.currentRunId });
        if (this.textMessageId) {
          chunks.push({
            type: 'message-metadata',
            messageMetadata: {
              textMessageId: this.textMessageId,
              pending: false,
            },
          });
        }
        return chunks;
      }

      case EventType.TEXT_MESSAGE_CONTENT: {
        const contentEvent = event as TextMessageContentEvent;
        const textChunks = this.citationParser.parse(contentEvent.delta);

        const chunks = textChunks
          .map((chunk) => ({
            type: 'text-delta' as const,
            delta: chunk,
            id: this.currentRunId,
          }))
          .filter((chunk) => chunk.delta);

        return chunks.length === 1 ? chunks[0] : chunks;
      }

      case EventType.TEXT_MESSAGE_CHUNK: {
        const chunkEvent = event as TextMessageChunkEvent;
        if (chunkEvent.delta) {
          const textChunks = this.citationParser.parse(chunkEvent.delta);

          const chunks = textChunks
            .map((chunk) => ({
              type: 'text-delta' as const,
              delta: chunk,
              id: this.currentRunId,
            }))
            .filter((chunk) => chunk.delta);

          return chunks.length === 1 ? chunks[0] : chunks;
        }
        break;
      }

      case EventType.TEXT_MESSAGE_END: {
        const chunks: ChatUIMessageChunk[] = [];
        const remaining = this.citationParser.flush();

        if (remaining) {
          chunks.push({
            type: 'text-delta',
            delta: remaining,
            id: this.currentRunId,
          });
        }

        chunks.push({ type: 'text-end', id: this.currentRunId });
        return chunks;
      }

      case EventType.TOOL_CALL_START: {
        const toolStartEvent = event as ToolCallStartEvent;
        const aiEvent: ChatUIMessageChunk = {
          type: 'tool-input-start',
          toolCallId: toolStartEvent.toolCallId,
          toolName: toolStartEvent.toolCallName,
        };
        // curator-chatbot adds a humanized toolCallDisplayName. AI SDK tool
        // parts have no title field, so it rides on a per-run data part.
        // Checks runId directly: currentRunId throws, which would drop the tool start.
        if (
          this.runId !== null &&
          'toolCallDisplayName' in toolStartEvent &&
          typeof toolStartEvent.toolCallDisplayName === 'string'
        ) {
          this.accumulatedToolDisplayNames = {
            ...this.accumulatedToolDisplayNames,
            [toolStartEvent.toolCallName]: toolStartEvent.toolCallDisplayName,
          };
          const displayNamesEvent: ToolDisplayNamesUIMessageChunk = {
            type: 'data-tool_display_names',
            id: this.runId,
            data: this.accumulatedToolDisplayNames,
          };
          return [displayNamesEvent, aiEvent];
        }
        return aiEvent;
      }

      case EventType.TOOL_CALL_ARGS: {
        return null;
      }

      case EventType.TOOL_CALL_END: {
        return null;
      }

      case EventType.TOOL_CALL_RESULT: {
        const toolResultEvent = event as ToolCallResultEvent;
        const aiEvent: ChatUIMessageChunk = {
          type: 'tool-output-available',
          toolCallId: toolResultEvent.toolCallId,
          output: toolResultEvent.content,
        };
        return aiEvent;
      }

      case EventType.CUSTOM: {
        const customEvent = event as CustomEvent;
        if (customEvent.name === 'CITATION_SOURCE') {
          const { source_index, source } =
            customEvent.value as CitationSourceEvent;
          // Accumulate citations in sparse array
          this.accumulatedCitations[source_index - 1] = source;
          const aiEvent: CitedChatUIMessageChunk = {
            type: 'data-citation_sources',
            id: this.currentRunId,
            data: [...this.accumulatedCitations],
          };
          return aiEvent;
        }
        if (customEvent.name === 'CITATION_SOURCES') {
          const { sources: data } = customEvent.value as CitationSourcesEvent;
          const aiEvent: CitedChatUIMessageChunk = {
            type: 'data-citation_sources',
            id: this.currentRunId,
            data,
          };
          return aiEvent;
        }
        return null;
      }

      case EventType.RUN_ERROR: {
        const errorEvent = event as RunErrorEvent;
        const aiEvent: ChatUIMessageChunk = {
          type: 'error',
          errorText: errorEvent.message,
        };
        return aiEvent;
      }

      case EventType.RUN_FINISHED: {
        this.runId = null;
        this.textMessageId = null;
        const aiEvent: ChatUIMessageChunk = { type: 'finish' };
        return aiEvent;
      }
    }
    return null;
  }
}
